import { Component, OnInit } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { FcpIconSVGSizes, IconNameMap } from 'fc-ui-components';
import { AutoUnsubscribe, getSupportEmail } from 'helpers';
import { AuthenticationModel } from 'src/app/authentication/authentication.model';
import { CompatibleHttpErrorResponse } from 'shared/models/abstract-service-api.model';
import { CrmServiceModel } from 'shared/models/crm-service.model';

@Component({
    selector: 'fc-signup-verify-email',
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.less'],
    standalone: false
})
@AutoUnsubscribe
export class SignupVerifyEmailComponent implements OnInit {
    iconNameMap = IconNameMap;
    fcpIconSVGSize = FcpIconSVGSizes;
    supportEmail = getSupportEmail();
    userEmail$: Observable<string>;
    isResendInFlight = false;
    isResendDisabledForSession = false;
    showResendError = false;

    private _appUser$: Observable<User>;
    private _subscription$ = new Subscription();

    constructor(
        private authenticationModel: AuthenticationModel,
        private authService: AuthService,
        private crmServiceModel: CrmServiceModel,
        private router: Router
    ) {}

    ngOnInit(): void {
        this._appUser$ = this.authService.user$;

        this.userEmail$ = this._appUser$.pipe(map((user) => user?.email));

        this._subscription$.add(
            this._appUser$
                .pipe(
                    filter(() => this.router.url === '/signup/verify-email'),
                    switchMap((user) => {
                        // Includes AppUser user-metadata
                        return this.authenticationModel.getAppUser(user.sub);
                    })
                )
                .subscribe((user) => {
                    const redirectionPath = _.get(user, 'userMetadata.redirectionPath');
                    if (redirectionPath && !redirectionPath.includes('finish')) {
                        return this.router.navigate([redirectionPath, 'verify-email']);
                    }
                })
        );
    }

    resendVerificationEmail(): void {
        if (this.isResendInFlight || this.isResendDisabledForSession) {
            return;
        }

        this.isResendInFlight = true;
        this.showResendError = false;

        this._subscription$.add(
            this.crmServiceModel.resendEmailVerification().subscribe({
                next: () => {
                    this.isResendInFlight = false;
                    this.isResendDisabledForSession = true;
                },
                error: (error: CompatibleHttpErrorResponse) => {
                    if (error.status === 409) {
                        this._subscription$.add(
                            this.authService.getAccessTokenSilently({ cacheMode: 'off' }).subscribe({
                                next: () => {
                                    this.router.navigateByUrl(this.router.url).finally(() => {
                                        this.isResendInFlight = false;
                                    });
                                },
                                error: () => {
                                    this.isResendInFlight = false;
                                    this.showResendError = true;
                                }
                            })
                        );
                        return;
                    }

                    this.isResendInFlight = false;
                    this.showResendError = true;
                }
            })
        );
    }
}
